import type { ReactNode } from 'react';
import { Boton } from './Boton';

/**
 * Que algo falló, dicho de una sola forma en toda la aplicación, con `role="alert"` para que un
 * lector de pantalla lo anuncie.
 *
 * Devuelve `null` sin contenido, para que las páginas no tengan que envolverlo en `{error && ...}`.
 */
export function MensajeError({
  children,
  caja = false,
  className = '',
  e2e,
}: {
  children?: ReactNode;
  /** Con fondo teñido y relleno: para el error que resume un formulario entero. */
  caja?: boolean;
  className?: string;
  e2e?: string;
}) {
  if (!children) return null;
  return (
    <p
      role="alert"
      data-e2e={e2e}
      className={`text-destructive text-contenido font-semibold ${caja ? 'bg-destructive/10 rounded-control px-3 py-2' : ''} ${className}`}
    >
      {children}
    </p>
  );
}

/**
 * El patrón A4: qué no se pudo leer, por qué —la conexión, o el motivo que traduce `ErrorDeApi` si el
 * servidor respondió— y volver a pedirlo. Sin error no pinta nada.
 */
export function ErrorDeLectura({
  que,
  error,
  onReintentar,
  e2e,
}: {
  /** Lo que no llegó, como sigue a «No pudimos leer»: «las cuentas del negocio». */
  que: string;
  error: unknown;
  onReintentar: () => void;
  e2e?: string;
}) {
  if (!error) return null;
  // Status 0 es una lectura que no llegó al servidor: el motivo es la conexión del equipo.
  const sinRed = !(error instanceof Error) || !error.message || (error as { status?: number }).status === 0;
  const motivo = sinRed ? 'Revisa la conexión.' : error.message;
  return (
    <div className="flex flex-wrap items-center gap-3">
      <MensajeError caja e2e={e2e}>
        ⚠ No pudimos leer {que}. {motivo}
      </MensajeError>
      <Boton variante="secundario" onClick={onReintentar}>
        Reintentar
      </Boton>
    </div>
  );
}

/**
 * Que algo salió bien: la misma forma que `MensajeError` con otro tono.
 */
export function MensajeExito({ children, e2e }: { children?: ReactNode; e2e?: string }) {
  if (!children) return null;
  return (
    <p
      role="status"
      data-e2e={e2e}
      className="text-success bg-success/10 rounded-control px-3 py-2 text-contenido font-semibold"
    >
      {children}
    </p>
  );
}

/** La línea que explica de qué va la pantalla, bajo el título. */
export function TextoAyuda({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <p className={`text-muted mb-3 text-contenido ${className}`}>{children}</p>;
}
